import React from "react"
import Footer from "../../components/marketing/footer/Footer"
import heroBg from "../../assets/images/marketplace/splash-background.jpeg"
import MarketplaceProfileCard from "../../components/cards/MarketplaceProfileCard"
import useEmblaCarousel from "embla-carousel-react"
import useScrollToTop from "../../hooks/useScrollToTop"
import { Button } from "../../components/ui/button"
import { CoachCard, CoachTagWithImage } from "../../models/public.interface"
import { useSearchParams } from "react-router-dom"
import { PublicProfileRequests } from "../../api/public.service"
import { SearchMarketplace } from "../../components/navigation/SearchMarketplace"
import { SvgChevronLeft, SvgChevronRight } from "../../components/icons/"
import { useEffect, useState } from "react"
import { NoCoachesFound } from "./_components/no-coaches-found"
import { CategorySlide } from "./_components/category-slide"
import SEO from "../(marketing)/_components/seo"

export function FindACoachPage() {
  useScrollToTop()
  // State
  const [tags, setTags] = useState<CoachTagWithImage[]>([])
  const [coaches, setCoaches] = useState<CoachCard[]>([])
  const [hasNextPage, setHasNextPage] = useState(false)

  // Hooks
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: "auto",
    loop: true,
  })
  const [searchParams, setSearchParams] = useSearchParams()
  // Functions
  const scrollPrev = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  async function getCoachTags() {
    try {
      let tags = await PublicProfileRequests.getCoachTags()
      tags = tags.filter((tag) => tag.coach_count > 0)

      const tagsWithImagePromises = tags.map(async (tag) => {
        const image = await import(
          `../../assets/images/marketplace/categories/${tag.id}.jpg`
        )
        return {
          ...tag,
          image: image.default,
        }
      })

      const tagsWithImage = await Promise.all(tagsWithImagePromises)

      setTags(tagsWithImage)
    } catch (error) {
      console.error(error)
    }
  }

  const getCoaches = async () => {
    try {
      const coaches = await PublicProfileRequests.getCoaches(
        searchParams.get("q") || "",
        searchParams.getAll("tags").map((tag) => parseInt(tag)),
        parseInt(searchParams.get("page") || "1"),
        null,
        1
      )

      setCoaches(coaches)

      const nextPageCoaches = await PublicProfileRequests.getCoaches(
        searchParams.get("q") || "",
        searchParams.getAll("tags").map((tag) => parseInt(tag)),
        parseInt(searchParams.get("page") || "1") + 1,
        null,
        1
      )

      setHasNextPage(nextPageCoaches.length > 0)
    } catch (error) {
      console.error(error)
    }
  }

  // UseEffects
  useEffect(() => {
    getCoachTags()
  }, [])

  useEffect(() => {
    getCoaches()
  }, [searchParams])

  return (
    <>
      <SEO
        title="Find a Coach Online | Zoee"
        description="Find your perfect match on Zoee's network of independent coaches. Whether you need an Executive, Life, Spiritual, or another type of coach, we can help."
      />{" "}
      {!Boolean(
        searchParams.getAll("tags").length || searchParams.get("q")
      ) && (
        <section
          className="-mt-16 mb-12 bg-cover"
          style={{
            background: `linear-gradient(357deg, rgba(236, 240, 255, 0.25) -20.82%, #F8F8F8 97.75%), url(${heroBg}), lightgray 50% / cover no-repeat`,
          }}
        >
          <div className="container flex max-w-screen-xl flex-col gap-8 pb-12 pt-36">
            <h1 className="text-3xl font-bold sm:text-3xl md:text-4xl lg:text-5xl">
              Find a Coach Online With Zoee
            </h1>
            <p className="text-xl sm:text-2xl md:w-2/3 md:text-3xl">
              One platform - many voices! From Executive Coaching to Spiritual
              Coaching and everything in between, Zoee is on a mission to shape
              a world where every life is profoundly impacted! Discover how easy
              it is to find a coach online.
            </p>

            <div className="-mt-6 flex flex-col gap-4 sm:-mt-10">
              <div className="ml-auto flex gap-4">
                <Button
                  onClick={scrollPrev}
                  size="icon"
                  className="rounded-full"
                  variant="ghost"
                  aria-label="Scroll To Previous"
                >
                  <SvgChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  onClick={scrollNext}
                  size="icon"
                  className="rounded-full"
                  variant="ghost"
                  aria-label="Scroll To Next"
                >
                  <SvgChevronRight className="h-6 w-6" />
                </Button>
              </div>

              <div className="overflow-hidden" ref={emblaRef}>
                <div className="-mr-4 flex">
                  {tags.map((tag) => (
                    <CategorySlide {...tag} key={tag.id} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      <section className="container my-12 grid grid-cols-1 gap-8 sm:grid-cols-2  md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <div className="col-span-full lg:hidden">
          <SearchMarketplace />
        </div>
        {coaches.length ? (
          <>
            {coaches.map((coach) => (
              <MarketplaceProfileCard
                {...coach}
                key={coach.coach_public_profile_id}
              />
            ))}
          </>
        ) : (
          <NoCoachesFound />
        )}
      </section>
      <Footer />
    </>
  )
}
